import { useState } from 'react';
import type { ReactNode } from 'react';
import { Alert, IconButton, Stack, Tooltip } from '@mui/material';
import type { AlertProps } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';

/**
 * Drop-in replacement for MUI <Alert severity="error"> that adds a
 * copy-to-clipboard icon to the right edge. Project rule: every error
 * surface must let the user grab the message verbatim — long stack
 * traces and HTTP-error strings are the most common follow-up the
 * user wants to send to support / paste into a search.
 *
 * Accepts either a string `message` (default; copies the string) or a
 * `children` block (copies whatever its trimmed `textContent` is).
 *
 * Other Alert props (`severity`, `variant`, `sx`, ...) pass through.
 * `severity` defaults to "error" since that's the use case; pass
 * "warning" / "info" to change.
 */
export type ErrorAlertProps = Omit<AlertProps, 'children' | 'severity'> & {
  severity?: AlertProps['severity'];
  message?: string | null | undefined;
  children?: ReactNode;
};

export function ErrorAlert({
  message, children, severity = 'error', sx,
  // Pulled out of `rest` so a caller-supplied action (e.g. RouteDataGrid's
  // Retry button) composes WITH the copy button instead of replacing it.
  // Before this fix the spread `{...rest}` overwrote `action`, so any list
  // page that passed Retry silently lost the copy affordance — exactly
  // the surfaces where users most want to grab the verbatim error.
  action: extraAction,
  ...rest
}: ErrorAlertProps) {
  const [copied, setCopied] = useState(false);

  // Always render *something* so the user never sees a blank pink line.
  const text = (message ?? '').toString().trim();
  const display: ReactNode = (children ?? text) || 'An error occurred (no further detail from server).';

  async function copy() {
    let toCopy = text;
    if (!toCopy && children != null) {
      // Fall back to a best-effort string of any ReactNode children.
      toCopy = String(children);
    }
    if (!toCopy) return;
    try {
      await navigator.clipboard.writeText(toCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked — silent */ }
  }

  const copyButton = (
    <Tooltip title={copied ? 'Copied' : 'Copy'}>
      <IconButton size="small" color="inherit" onClick={copy} aria-label="Copy error">
        {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
  );

  const action = extraAction
    ? (
      <Stack direction="row" spacing={0.5} alignItems="center">
        {extraAction}
        {copyButton}
      </Stack>
    )
    : copyButton;

  return (
    <Alert
      severity={severity}
      sx={{
        // Make multi-line errors (server stack traces) readable rather
        // than a single squashed line.
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        ...sx,
      }}
      {...rest}
      action={action}
    >
      {display}
    </Alert>
  );
}
